import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../common/Layout';
import QuestionCard from './QuestionCard';
import { calculateScore } from '../../utils/scoring';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface Question {
  id: string;
  question: string;
  weight: number;
}

const QuestionnaireScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [session, setSession] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const profileId = location.state?.profileId;
  
  useEffect(() => {
    // Get current session
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };
    
    // Fetch questions from the database based on current language
    const fetchQuestions = async () => {
      try {
        const languageColumn = `question_${i18n.language}` as keyof Pick<Question, 'question'>;
        
        const { data, error } = await supabase
          .from('questionnaire_questions')
          .select('id, weight, ' + languageColumn)
          .order('created_at', { ascending: true });
          
        if (error) {
          console.error('Error fetching questions:', error);
          toast({
            title: t('error'),
            description: t('errorLoadingData'),
            variant: "destructive",
          });
          return;
        }
        
        if (data && data.length > 0) {
          // Transform the data to match the Question interface
          const transformedQuestions = data.map(item => ({
            id: item.id,
            question: item[languageColumn],
            weight: item.weight
          }));
          setQuestions(transformedQuestions);
        } else {
          // Use default questions if none found in DB
          const defaultQuestions = [
            {
              id: '1',
              question: t('questionnaireQuestions.1'),
              weight: 20
            },
            {
              id: '2',
              question: t('questionnaireQuestions.2'),
              weight: 20
            },
            {
              id: '3',
              question: t('questionnaireQuestions.3'),
              weight: 20
            },
            {
              id: '4',
              question: t('questionnaireQuestions.4'),
              weight: 20
            },
            {
              id: '5',
              question: t('questionnaireQuestions.5'),
              weight: 20
            }
          ];
          setQuestions(defaultQuestions);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
      }
    };
    
    getSession();
    fetchQuestions();
  }, [toast, i18n.language]);
  
  const handleAnswer = async (answer: boolean) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Calculate score based on weighted questions
      const weightedScore = calculateWeightedScore(newAnswers, questions);
      const resultCategory = getResultCategory(weightedScore);
      
      // Save to localStorage for non-logged-in users too
      localStorage.setItem('lastAnemiaScore', weightedScore.toString());
      localStorage.setItem('lastAnemiaCategory', resultCategory);
      
      // If user is logged in and a profile was selected, save results to DB
      if (session && profileId && profileId !== 'guest') {
        await saveResultsToDatabase(weightedScore, resultCategory, newAnswers, profileId);
      }
      
      // Navigate to results
      navigate('/result', { 
        state: { 
          score: weightedScore, 
          answers: newAnswers,
          resultCategory: resultCategory,
          resultIconType: getResultIconType(weightedScore),
          resultColor: getResultColor(weightedScore)
        } 
      });
    }
  };
  
  const calculateWeightedScore = (answers: boolean[], questions: Question[]): number => {
    let totalWeight = 0;
    let weightedYesCount = 0;
    
    answers.forEach((answer, index) => {
      const questionWeight = questions[index]?.weight || 20;
      totalWeight += questionWeight;
      
      if (answer) { // If the answer is "yes" (indicating symptom)
        weightedYesCount += questionWeight;
      }
    });
    
    // Calculate percentage of "no" answers (100 - percentage of "yes" answers)
    return Math.round(100 - (weightedYesCount / totalWeight * 100));
  };
  
  const getResultCategory = (score: number): string => {
    if (score < 50) return 'Alto riesgo';
    if (score < 80) return 'Riesgo moderado';
    return 'Bajo riesgo';
  };
  
  const getResultIconType = (score: number): 'smile' | 'meh' | 'frown' => {
    if (score < 50) return 'frown';
    if (score < 80) return 'meh';
    return 'smile';
  };
  
  const getResultColor = (score: number): string => {
    if (score < 50) return 'sangre';
    if (score < 80) return 'warning';
    return 'fuerte';
  };
  
  const saveResultsToDatabase = async (score: number, category: string, answers: boolean[], profileId: string) => {
    try {
      if (!session) return;
      
      const userId = session.user.id;
      
      // Create structured answers object with question IDs
      const answersObject = questions.map((question, index) => ({
        question_id: question.id,
        question_text: question.question,
        answer: answers[index] || false
      }));
      
      // Save to questionnaire_results table
      const { error } = await supabase
        .from('questionnaire_results')
        .insert({
          user_id: userId,
          profile_id: profileId,
          score: score,
          answers: answersObject
        });
      
      if (error) {
        console.error('Error saving results:', error);
        toast({
          title: "Error",
          description: "No se pudieron guardar los resultados",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Resultados guardados",
        description: `Los resultados se han guardado para el perfil seleccionado`,
      });
    } catch (error) {
      console.error('Error saving results:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los resultados",
        variant: "destructive",
      });
    }
  };
  
  if (loading) {
    return (
      <Layout title={t('loading')} showBackButton={true}>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-10 h-10 text-sangre-500 animate-spin" />
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout 
      title={t('questionnaire')} 
      showBackButton={true} 
      className="flex flex-col justify-center items-center"
    >
      {questions.length > 0 && (
        <QuestionCard
          question={questions[currentQuestionIndex].question}
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          onYes={() => handleAnswer(true)}
          onNo={() => handleAnswer(false)}
        />
      )}
    </Layout>
  );
};

export default QuestionnaireScreen;
